import type { GlobalSettings, Segment, Workflow } from '../types'

const G_VAE = '1'
const G_CLIP = '2'
const G_CLIPVIS = '3'
const G_IMG_START = '4'
const G_SCALE = '5'
const G_CLIPVIS_ENC = '6'
const G_VAE_ENC = '7'
const G_IMG_END = '8'
const G_VAE_ENC_END = '9'
const G_UNET_HIGH = '10'
const G_UNET_LOW = '11'
const G_ACCEL_LORA = '12'
const G_NEG = '13'
const G_SCALE_END = '14'
const G_CLIPVIS_ENC_END = '15'

const O_LORA_H = 0
const O_LORA_L = 1
const O_SAGE_H = 2
const O_SAGE_L = 3
const O_TORCH_H = 4
const O_TORCH_L = 5
const O_SAMP_H = 6
const O_SAMP_L = 7
const O_PROMPT = 8
const O_WAN = 9
const O_KS_H = 10
const O_KS_L = 11
const O_CUT = 12
const O_VAE_DEC = 13
const O_SVI_H = 14
const O_SVI_L = 15
const O_END_IMG = 16
const O_SCALE_END = 17
const O_VAE_ENC_END = 18
const O_CLIPVIS_END = 19
const O_START_INHERIT = 20
const O_CLIPVIS_START = 21

type Ref = [string, number]

export const sid = (segmentIndex: number, offset: number): string =>
  String(1000 + segmentIndex * 100 + offset)
const cid = (index: number): string => String(2000 + index)
const ref = (nodeId: string, index = 0): Ref => [nodeId, index]
const isRealLora = (name: unknown): boolean =>
  typeof name === 'string' && Boolean(name.trim()) && name.trim().toLowerCase() !== 'none'

const randomSeed = (): number => {
  const values = crypto.getRandomValues(new Uint32Array(2))
  return (values[0] & 0x00ff_ffff) * 0x01_00_00_00 + (values[1] & 0x00ff_ffff)
}

export const effectiveSeed = (seed: number): number => seed >= 0 ? seed : randomSeed()

export const decodedFrameCount = (segment: Segment): number => {
  const latentCount = Math.trunc((segment.frame_count - 1) / 4) + 1
  const decodedLatentCount = segment.node_type === 'sviflf'
    ? latentCount - 1
    : latentCount
  return 1 + (decodedLatentCount - 1) * 4
}

const workflowTimestamp = (date = new Date()): string => {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}/${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
}

const addNode = (
  nodes: Workflow,
  id: string,
  classType: string,
  title: string,
  inputs: Record<string, unknown>,
): void => {
  nodes[id] = { inputs, class_type: classType, _meta: { title } }
}

const addCommonGlobalNodes = (nodes: Workflow, gs: GlobalSettings): string => {
  addNode(nodes, G_VAE, 'VAELoader', 'Load VAE', { vae_name: gs.vae })
  addNode(nodes, G_CLIP, 'CLIPLoader', 'Load CLIP', { clip_name: gs.clip, type: 'wan', device: 'default' })
  addNode(nodes, G_CLIPVIS, 'CLIPVisionLoader', 'Load CLIP Vision', { clip_name: gs.clip_vision })
  addNode(nodes, G_IMG_START, 'LoadImage', 'Start Image', { image: gs.start_image })
  addNode(nodes, G_SCALE, 'LayerUtility: ImageScaleByAspectRatio V2', 'Scale Image', {
    aspect_ratio: 'original', proportional_width: 1, proportional_height: 1,
    fit: 'crop', method: 'lanczos', round_to_multiple: '16', scale_to_side: 'longest',
    scale_to_length: gs.resolution, background_color: '#000000', image: ref(G_IMG_START),
  })
  addNode(nodes, G_CLIPVIS_ENC, 'CLIPVisionEncode', 'CLIP Vision Encode', {
    crop: 'none', clip_vision: ref(G_CLIPVIS), image: ref(G_SCALE),
  })
  addNode(nodes, G_UNET_HIGH, 'UNETLoader', 'UNet High', {
    unet_name: gs.unet_high, weight_dtype: 'fp8_e4m3fn_fast',
  })
  addNode(nodes, G_UNET_LOW, 'UNETLoader', 'UNet Low', {
    unet_name: gs.unet_low, weight_dtype: 'fp8_e4m3fn_fast',
  })
  let accelReference = G_UNET_HIGH
  if (isRealLora(gs.accel_lora)) {
    addNode(nodes, G_ACCEL_LORA, 'LoraLoaderModelOnly', 'Accel LoRA', {
      lora_name: gs.accel_lora,
      strength_model: Number(gs.accel_lora_strength),
      model: ref(G_UNET_HIGH),
    })
    accelReference = G_ACCEL_LORA
  }
  addNode(nodes, G_NEG, 'CLIPTextEncode', 'Negative Prompt', {
    text: gs.negative_prompt, clip: ref(G_CLIP),
  })
  return accelReference
}

const loraStackInputs = (
  model: Ref,
  rows: Segment['lora_high'],
): Record<string, unknown> => {
  const inputs: Record<string, unknown> = { model, clip: ref(G_CLIP) }
  rows.slice(0, 4).forEach((lora, index) => {
    const suffix = String(index + 1).padStart(2, '0')
    const name = lora.name || ''
    inputs[`lora_${suffix}`] = name || 'None'
    inputs[`strength_${suffix}`] = name ? Number(lora.strength) : 0
  })
  return inputs
}

const addSegmentModelChain = (
  nodes: Workflow,
  gs: GlobalSettings,
  segment: Segment,
  index: number,
  accelReference: string,
  useSviLora: boolean,
  titlePrefix: string,
): void => {
  addNode(nodes, sid(index, O_LORA_H), 'Lora Loader Stack (rgthree)', `${titlePrefix} LoRA High`,
    loraStackInputs(ref(accelReference), segment.lora_high))
  addNode(nodes, sid(index, O_LORA_L), 'Lora Loader Stack (rgthree)', `${titlePrefix} LoRA Low`,
    loraStackInputs(ref(G_UNET_LOW), segment.lora_low))

  let highSource = ref(sid(index, O_LORA_H))
  let lowSource = ref(sid(index, O_LORA_L))
  if (useSviLora && isRealLora(gs.svi_lora_high)) {
    addNode(nodes, sid(index, O_SVI_H), 'LoraLoaderModelOnly', `${titlePrefix} SVI LoRA High`, {
      lora_name: gs.svi_lora_high,
      strength_model: Number(gs.svi_lora_strength),
      model: ref(sid(index, O_LORA_H)),
    })
    highSource = ref(sid(index, O_SVI_H))
  }
  if (useSviLora && isRealLora(gs.svi_lora_low)) {
    addNode(nodes, sid(index, O_SVI_L), 'LoraLoaderModelOnly', `${titlePrefix} SVI LoRA Low`, {
      lora_name: gs.svi_lora_low,
      strength_model: Number(gs.svi_lora_strength),
      model: ref(sid(index, O_LORA_L)),
    })
    lowSource = ref(sid(index, O_SVI_L))
  }

  addNode(nodes, sid(index, O_SAGE_H), 'PathchSageAttentionKJ', `${titlePrefix} Sage High`, {
    sage_attention: 'auto', allow_compile: false, model: highSource,
  })
  addNode(nodes, sid(index, O_SAGE_L), 'PathchSageAttentionKJ', `${titlePrefix} Sage Low`, {
    sage_attention: 'auto', allow_compile: false, model: lowSource,
  })
  addNode(nodes, sid(index, O_TORCH_H), 'ModelPatchTorchSettings', `${titlePrefix} Torch High`, {
    enable_fp16_accumulation: true, model: ref(sid(index, O_SAGE_H)),
  })
  addNode(nodes, sid(index, O_TORCH_L), 'ModelPatchTorchSettings', `${titlePrefix} Torch Low`, {
    enable_fp16_accumulation: true, model: ref(sid(index, O_SAGE_L)),
  })
  addNode(nodes, sid(index, O_SAMP_H), 'ModelSamplingSD3', `${titlePrefix} Sampling High`, {
    shift: Number(gs.shift), model: ref(sid(index, O_TORCH_H)),
  })
  addNode(nodes, sid(index, O_SAMP_L), 'ModelSamplingSD3', `${titlePrefix} Sampling Low`, {
    shift: Number(gs.shift), model: ref(sid(index, O_TORCH_L)),
  })
  addNode(nodes, sid(index, O_PROMPT), 'CLIPTextEncode', `${titlePrefix} Prompt`, {
    text: segment.prompt, clip: ref(sid(index, O_LORA_H), 1),
  })
}

const addSamplersAndDecode = (
  nodes: Workflow,
  gs: GlobalSettings,
  index: number,
  seed: number,
  cutLastSlot: boolean,
  titlePrefix: string,
): void => {
  addNode(nodes, sid(index, O_KS_H), 'KSamplerAdvanced', `${titlePrefix} KSampler High`, {
    add_noise: 'enable', noise_seed: seed, steps: gs.total_steps, cfg: Number(gs.cfg),
    sampler_name: gs.sampler, scheduler: gs.scheduler, start_at_step: 0,
    end_at_step: gs.high_steps, return_with_leftover_noise: 'enable',
    model: ref(sid(index, O_SAMP_H)), positive: ref(sid(index, O_WAN), 0),
    negative: ref(sid(index, O_WAN), 1), latent_image: ref(sid(index, O_WAN), 2),
  })
  addNode(nodes, sid(index, O_KS_L), 'KSamplerAdvanced', `${titlePrefix} KSampler Low`, {
    add_noise: 'disable', noise_seed: seed, steps: gs.total_steps, cfg: 1,
    sampler_name: gs.sampler, scheduler: gs.scheduler, start_at_step: gs.high_steps,
    end_at_step: 10000, return_with_leftover_noise: 'disable',
    model: ref(sid(index, O_SAMP_L)), positive: ref(sid(index, O_WAN), 0),
    negative: ref(sid(index, O_WAN), 1), latent_image: ref(sid(index, O_KS_H)),
  })
  let latent: Ref = ref(sid(index, O_KS_L))
  if (cutLastSlot) {
    addNode(nodes, sid(index, O_CUT), 'WanCutLastSlot', `${titlePrefix} Cut Last Slot`, {
      slots_to_cut: 1, latents: ref(sid(index, O_KS_L)),
    })
    latent = ref(sid(index, O_CUT))
  }
  addNode(nodes, sid(index, O_VAE_DEC), 'VAEDecode', `${titlePrefix} VAE Decode`, {
    samples: latent, vae: ref(G_VAE),
  })
}

const addOutput = (
  nodes: Workflow,
  gs: GlobalSettings,
  images: Ref,
  prefix: 'wan_i2v' | 'wan_loop',
  timestamp: string,
): void => {
  let finalImages = images
  if (gs.color_match) {
    addNode(nodes, '2100', 'ColorMatchV2', 'Color Match', {
      method: gs.color_match_mode,
      strength: Number(gs.color_match_strength),
      multithread: true,
      image_target: images,
      image_ref: ref(G_SCALE),
    })
    finalImages = ref('2100')
  }
  addNode(nodes, '2101', 'VHS_VideoCombine', 'Video Output', {
    frame_rate: Number(gs.combine_fps), loop_count: 0,
    filename_prefix: `${prefix}/${timestamp}`, format: 'video/h264-mp4',
    pix_fmt: 'yuv420p', crf: 18, save_metadata: false, trim_to_audio: false,
    pingpong: false, save_output: true, images: finalImages,
  })
}

export function buildWorkflow(
  gs: GlobalSettings,
  segments: Segment[],
  options: { timestamp?: string; seed?: number } = {},
): Workflow {
  const nodes: Workflow = {}
  const seed = options.seed ?? effectiveSeed(gs.seed)
  const timestamp = options.timestamp ?? workflowTimestamp()
  const accelReference = addCommonGlobalNodes(nodes, gs)
  addNode(nodes, G_VAE_ENC, 'VAEEncode', 'VAE Encode Anchor', {
    pixels: ref(G_SCALE), vae: ref(G_VAE),
  })
  let latestAnchorSamples: Ref = ref(G_VAE_ENC)

  segments.forEach((segment, index) => {
    const nodeType = segment.node_type
    const anchorSamples = latestAnchorSamples
    const title = `Seg${index + 1}`
    addSegmentModelChain(nodes, gs, segment, index, accelReference,
      nodeType === 'svi' || nodeType === 'sviflf', title)

    const hasEndOverride = Boolean(segment.end_image && segment.end_image.toLowerCase() !== 'none')
    let endLatent: Ref | undefined
    let endImage: Ref | undefined
    let endClipVision: Ref | undefined
    if (hasEndOverride) {
      addNode(nodes, sid(index, O_END_IMG), 'LoadImage', `${title} End Image (override)`, {
        image: segment.end_image,
      })
      addNode(nodes, sid(index, O_SCALE_END), 'LayerUtility: ImageScaleByAspectRatio V2', `${title} Scale End`, {
        aspect_ratio: 'original', proportional_width: 1, proportional_height: 1,
        fit: 'crop', method: 'lanczos', round_to_multiple: '16', scale_to_side: 'longest',
        scale_to_length: gs.resolution, background_color: '#000000', image: ref(sid(index, O_END_IMG)),
      })
      if (nodeType === 'sviflf' || nodeType === 'flf2v') {
        addNode(nodes, sid(index, O_VAE_ENC_END), 'VAEEncode', `${title} VAE Encode End (override)`, {
          pixels: ref(sid(index, O_SCALE_END)), vae: ref(G_VAE),
        })
      }
      if (nodeType === 'sviflf') {
        endLatent = ref(sid(index, O_VAE_ENC_END))
      }
      if (nodeType === 'flf2v') {
        addNode(nodes, sid(index, O_CLIPVIS_END), 'CLIPVisionEncode', `${title} CLIP Vision Encode End (override)`, {
          crop: 'none', clip_vision: ref(G_CLIPVIS), image: ref(sid(index, O_SCALE_END)),
        })
        endImage = ref(sid(index, O_SCALE_END))
        endClipVision = ref(sid(index, O_CLIPVIS_END))
      }
    }
    if (nodeType === 'sviflf' && !endLatent) endLatent = ref(G_VAE_ENC)
    if (nodeType === 'flf2v' && !endImage) {
      endImage = ref(G_SCALE)
      endClipVision = ref(G_CLIPVIS_ENC)
    }

    let startImage = ref(G_SCALE)
    let startClipVision = ref(G_CLIPVIS_ENC)
    if (nodeType === 'flf2v' && index > 0) {
      const previous = segments[index - 1]
      const previousHasEndOverride = Boolean(
        previous.end_image && previous.end_image.toLowerCase() !== 'none',
      )
      if (previousHasEndOverride) {
        startImage = ref(sid(index - 1, O_SCALE_END))
      } else {
        addNode(nodes, sid(index, O_START_INHERIT), 'ImageFromBatch', `${title} Inherit Start Frame`, {
          image: ref(sid(index - 1, O_VAE_DEC)),
          batch_index: decodedFrameCount(previous) - 1,
          length: 1,
        })
        startImage = ref(sid(index, O_START_INHERIT))
      }
      addNode(nodes, sid(index, O_CLIPVIS_START), 'CLIPVisionEncode', `${title} CLIP Vision Encode Start`, {
        crop: 'none', clip_vision: ref(G_CLIPVIS), image: startImage,
      })
      startClipVision = ref(sid(index, O_CLIPVIS_START))
    }

    if (nodeType === 'i2v') {
      addNode(nodes, sid(index, O_WAN), 'WanImageToVideo', `${title} I2V`, {
        width: ref(G_SCALE, 3), height: ref(G_SCALE, 4), length: segment.frame_count,
        batch_size: 1, positive: ref(sid(index, O_PROMPT)), negative: ref(G_NEG),
        vae: ref(G_VAE), clip_vision_output: ref(G_CLIPVIS_ENC), start_image: ref(G_SCALE),
      })
    } else if (nodeType === 'flf2v') {
      addNode(nodes, sid(index, O_WAN), 'WanFirstLastFrameToVideo', `${title} FLF2V`, {
        width: ref(G_SCALE, 3), height: ref(G_SCALE, 4), length: segment.frame_count,
        batch_size: 1, positive: ref(sid(index, O_PROMPT)), negative: ref(G_NEG),
        vae: ref(G_VAE), clip_vision_start_image: startClipVision,
        clip_vision_end_image: endClipVision, start_image: startImage, end_image: endImage,
      })
    } else if (nodeType === 'sviflf') {
      addNode(nodes, sid(index, O_WAN), 'WanImageToVideoSVIProFLF', `${title} SVIFLF`, {
        length: segment.frame_count, motion_latent_count: 1,
        positive: ref(sid(index, O_PROMPT)), negative: ref(G_NEG),
        prev_samples: ref(sid(index - 1, O_KS_L)), anchor_samples: anchorSamples,
        end_samples: endLatent,
      })
    } else {
      addNode(nodes, sid(index, O_WAN), 'WanImageToVideoSVIPro', `${title} SVI`, {
        length: segment.frame_count, motion_latent_count: 1,
        positive: ref(sid(index, O_PROMPT)), negative: ref(G_NEG),
        prev_samples: ref(sid(index - 1, O_KS_L)), anchor_samples: anchorSamples,
      })
    }
    addSamplersAndDecode(nodes, gs, index, seed, nodeType === 'sviflf', title)
    if (hasEndOverride && (nodeType === 'flf2v' || nodeType === 'sviflf')) {
      latestAnchorSamples = ref(sid(index, O_VAE_ENC_END))
    }
  })

  let combined: Ref
  if (segments.length === 1) {
    combined = ref(sid(0, O_VAE_DEC))
  } else {
    addNode(nodes, cid(0), 'ImageBatchExtendWithOverlap', 'Combine Seg1+2', {
      overlap: segments[1].node_type === 'flf2v' ? 1 : Math.trunc(gs.overlap),
      overlap_side: 'source', overlap_mode: 'linear_blend',
      source_images: ref(sid(0, O_VAE_DEC)), new_images: ref(sid(1, O_VAE_DEC)),
    })
    for (let index = 2; index < segments.length; index += 1) {
      addNode(nodes, cid(index - 1), 'ImageBatchExtendWithOverlap', `Combine +Seg${index + 1}`, {
        overlap: segments[index].node_type === 'flf2v' ? 1 : Math.trunc(gs.overlap),
        overlap_side: 'source', overlap_mode: 'linear_blend',
        source_images: ref(cid(index - 2), 2), new_images: ref(sid(index, O_VAE_DEC)),
      })
    }
    combined = ref(cid(segments.length - 2), 2)
  }

  const last = segments.at(-1)
  if (gs.use_rife_bridge && segments.length >= 2 && last?.node_type === 'sviflf') {
    const bridgeFrames = Math.max(1, Math.trunc(gs.rife_bridge_frames))
    addNode(nodes, '2200', 'ImageFromBatch', 'RIFE: Last FLF Frame', {
      image: ref(sid(segments.length - 1, O_VAE_DEC)), batch_index: decodedFrameCount(last) - 1, length: 1,
    })
    addNode(nodes, '2201', 'ImageBatch', 'RIFE: Input Pair', {
      image1: ref('2200'), image2: ref(G_SCALE),
    })
    addNode(nodes, '2202', 'RIFE VFI', 'RIFE: Loop Bridge', {
      ckpt_name: gs.rife_model || 'rife47.pth', frames: ref('2201'),
      clear_cache_after_n_frames: 10, multiplier: bridgeFrames + 1,
      fast_mode: true, ensemble: true, scale_factor: 1,
      dtype: 'float32', torch_compile: false, batch_size: 1,
    })
    addNode(nodes, '2203', 'ImageFromBatch', 'RIFE: Bridge Frames', {
      image: ref('2202'), batch_index: 1, length: bridgeFrames,
    })
    addNode(nodes, '2204', 'ImageBatch', 'Combined + RIFE Bridge', {
      image1: combined, image2: ref('2203'),
    })
    combined = ref('2204')
  }

  addOutput(nodes, gs, combined, 'wan_i2v', timestamp)
  return nodes
}

export function buildSimpleWorkflow(
  gs: GlobalSettings,
  segment: Segment,
  options: { timestamp?: string; seed?: number } = {},
): Workflow {
  const nodes: Workflow = {}
  const seed = options.seed ?? effectiveSeed(gs.seed)
  const timestamp = options.timestamp ?? workflowTimestamp()
  const accelReference = addCommonGlobalNodes(nodes, gs)
  nodes[G_SCALE]._meta.title = 'Scale Start'
  nodes[G_CLIPVIS_ENC]._meta.title = 'CLIP Vision Encode (start)'
  const nodeType = segment.node_type === 'i2v' ? 'i2v' : 'flf2v'
  let endImage: Ref | undefined
  let endClipVision: Ref | undefined
  if (nodeType === 'flf2v') {
    if (segment.end_image) {
      addNode(nodes, G_IMG_END, 'LoadImage', 'End Image', { image: segment.end_image })
      addNode(nodes, G_SCALE_END, 'LayerUtility: ImageScaleByAspectRatio V2', 'Scale End', {
        aspect_ratio: 'original', proportional_width: 1, proportional_height: 1,
        fit: 'crop', method: 'lanczos', round_to_multiple: '16', scale_to_side: 'longest',
        scale_to_length: gs.resolution, background_color: '#000000', image: ref(G_IMG_END),
      })
      addNode(nodes, G_CLIPVIS_ENC_END, 'CLIPVisionEncode', 'CLIP Vision Encode (end)', {
        crop: 'none', clip_vision: ref(G_CLIPVIS), image: ref(G_SCALE_END),
      })
      endImage = ref(G_SCALE_END)
      endClipVision = ref(G_CLIPVIS_ENC_END)
    } else {
      endImage = ref(G_SCALE)
      endClipVision = ref(G_CLIPVIS_ENC)
    }
  }

  addSegmentModelChain(nodes, gs, segment, 0, accelReference, false, 'Loop')
  if (nodeType === 'flf2v') {
    addNode(nodes, sid(0, O_WAN), 'WanFirstLastFrameToVideo', 'Simple WAN FLF2V', {
      width: ref(G_SCALE, 3), height: ref(G_SCALE, 4), length: segment.frame_count,
      batch_size: 1, positive: ref(sid(0, O_PROMPT)), negative: ref(G_NEG), vae: ref(G_VAE),
      clip_vision_start_image: ref(G_CLIPVIS_ENC), clip_vision_end_image: endClipVision,
      start_image: ref(G_SCALE), end_image: endImage,
    })
  } else {
    addNode(nodes, sid(0, O_WAN), 'WanImageToVideo', 'Simple WAN I2V', {
      width: ref(G_SCALE, 3), height: ref(G_SCALE, 4), length: segment.frame_count,
      batch_size: 1, positive: ref(sid(0, O_PROMPT)), negative: ref(G_NEG), vae: ref(G_VAE),
      clip_vision_output: ref(G_CLIPVIS_ENC), start_image: ref(G_SCALE),
    })
  }
  addSamplersAndDecode(nodes, gs, 0, seed, false, 'Loop')
  addOutput(nodes, gs, ref(sid(0, O_VAE_DEC)), 'wan_loop', timestamp)
  return nodes
}
